import { useEffect, useMemo, useRef, useState } from "react";
import { AlertCircle } from "lucide-react";
import { useRoster } from "@/hooks/useRoster";
import { cn } from "@/lib/cn";

interface PersonPickerProps {
  value: string;
  onChange: (name: string) => void;
  /** Limit suggestions to one role (e.g. "pm", "developer"). */
  roleFilter?: string;
  placeholder?: string;
  className?: string;
  /** Allow names not in the roster. Defaults to true so external names
   *  (vendors, ad-hoc stakeholders on a Brief) still work. When false,
   *  only roster matches are accepted. */
  allowFreeText?: boolean;
  /** Show a warning icon when value is non-empty and not a roster match.
   *  Defaults to true for free-text mode (a quiet "you typed a name we
   *  don't know" cue without blocking the save). */
  warnOffRoster?: boolean;
  disabled?: boolean;
  id?: string;
}

const INPUT_CLS =
  "w-full rounded-md border border-line px-2.5 py-1.5 text-sm focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-400";

export function PersonPicker({
  value,
  onChange,
  roleFilter,
  placeholder = "Name",
  className,
  allowFreeText = true,
  warnOffRoster = true,
  disabled = false,
  id,
}: PersonPickerProps) {
  const { data: roster = [] } = useRoster();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value);
  const [highlight, setHighlight] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Keep the typed query in sync when the parent resets the value
  // (e.g. dialog reopened, form reset). Otherwise stale text lingers.
  useEffect(() => {
    setQuery(value);
  }, [value]);

  // Outside-click closes the dropdown without committing whatever
  // partial query is in the box — the user already had a saved value.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const candidates = useMemo(() => {
    let list = roster;
    if (roleFilter) {
      list = list.filter((m) => m.role_key === roleFilter);
    }
    const q = query.trim().toLowerCase();
    if (!q) return list.slice(0, 12);
    return list
      .filter(
        (m) =>
          m.name.toLowerCase().includes(q) ||
          (m.role || "").toLowerCase().includes(q) ||
          (m.team || "").toLowerCase().includes(q),
      )
      .slice(0, 12);
  }, [roster, roleFilter, query]);

  const isOffRoster =
    !!value.trim() && !roster.some((m) => m.name.toLowerCase() === value.trim().toLowerCase());

  const commit = (name: string) => {
    onChange(name);
    setQuery(name);
    setOpen(false);
    setHighlight(0);
  };

  const handleBlur = () => {
    // Defer so a click on a dropdown item still registers.
    setTimeout(() => {
      if (!wrapperRef.current?.contains(document.activeElement)) {
        if (!allowFreeText) {
          const match = roster.find(
            (m) => m.name.toLowerCase() === query.trim().toLowerCase(),
          );
          if (match) {
            commit(match.name);
          } else {
            // Reject: revert to last known good value.
            setQuery(value);
            setOpen(false);
          }
        } else {
          onChange(query.trim());
          setOpen(false);
        }
      }
    }, 120);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(h + 1, candidates.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      if (open && candidates[highlight]) {
        e.preventDefault();
        commit(candidates[highlight].name);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} className={cn("relative", className)}>
      <div className="relative">
        <input
          id={id}
          type="text"
          value={query}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete="off"
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setHighlight(0);
            // Free-text mode: propagate every keystroke to the parent so a
            // typed name is captured even if the user clicks Save without
            // picking a suggestion or waiting for blur. (Previously typing
            // only updated local state, so type-then-Save lost the value —
            // the "assignee reverts" bug.) Non-free-text stays commit-only
            // so invalid names aren't pushed up.
            if (allowFreeText) onChange(e.target.value);
          }}
          onFocus={() => setOpen(true)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={cn(
            INPUT_CLS,
            warnOffRoster && isOffRoster && "pr-7",
            disabled && "bg-canvas text-ink-subtle",
          )}
        />
        {warnOffRoster && isOffRoster && (
          <AlertCircle
            className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-amber-500"
            aria-label="Not in roster"
          />
        )}
      </div>
      {open && candidates.length > 0 && (
        <ul className="absolute left-0 right-0 z-20 mt-1 max-h-60 overflow-y-auto rounded-md border border-line bg-surface shadow-lg">
          {candidates.map((m, idx) => {
            const active = idx === highlight;
            return (
              <li
                key={m.name}
                onMouseDown={(e) => {
                  e.preventDefault();
                  commit(m.name);
                }}
                onMouseEnter={() => setHighlight(idx)}
                className={cn(
                  "flex cursor-pointer items-center justify-between gap-3 px-2.5 py-1.5 text-sm",
                  active ? "bg-brand-navy-50 dark:bg-brand-navy-500/10 text-brand-navy-900 dark:text-brand-navy-300" : "text-ink-2 hover:bg-raised",
                )}
              >
                <span className="truncate font-medium">{m.name}</span>
                <span className="shrink-0 text-xs text-ink-subtle">
                  {m.role}
                  {m.team ? ` · ${m.team}` : ""}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
